import { useEffect, useState } from "react";
import { BadgePercent, Plus, Trash2 } from "lucide-react";

import { api, apiError } from "@/lib/api";
import { boliviaTodayInput } from "@/lib/dateTime";

const today = boliviaTodayInput();
const blankRule = {
  nombre: "",
  fecha_inicio: today,
  fecha_fin: today,
  tipo: "financiado",
  inicial_desde: 10,
  inicial_hasta: 100,
  meses_desde: 1,
  meses_hasta: 84,
  modo_descuento: "fijo",
  descuento_porcentaje: 10,
  base_descuento: "saldo",
  acumulable: false,
  activo: true,
};

function normalizeRule(rule) {
  return {
    ...blankRule,
    ...rule,
    inicial_desde: rule.inicial_desde ?? rule.inicial_minima ?? 0,
    inicial_hasta: rule.inicial_hasta ?? 100,
    meses_desde: rule.meses_desde ?? 1,
    meses_hasta: rule.meses_hasta ?? rule.meses_maximos ?? 120,
    descuento_porcentaje: rule.descuento_porcentaje ?? rule.descuento_saldo ?? 0,
  };
}

function ruleDescription(rule) {
  if (rule.modo_descuento === "igual_inicial") {
    return `Recibe lo que das · ${rule.inicial_desde}–${rule.inicial_hasta}% de inicial`;
  }
  if (rule.tipo === "contado") {
    return `Contado · ${rule.descuento_porcentaje}% sobre ${rule.base_descuento}`;
  }
  return `${rule.inicial_desde}–${rule.inicial_hasta}% inicial · ${rule.meses_desde}–${rule.meses_hasta} meses`;
}

export default function CommercialRulesPanel() {
  const [rules, setRules] = useState([]);
  const [rule, setRule] = useState(blankRule);
  const [message, setMessage] = useState("");

  const loadRules = () => {
    api.get("/promotions").then((response) => setRules(response.data.map(normalizeRule)));
  };

  useEffect(() => {
    loadRules();
  }, []);

  const change = (name, value) => {
    setRule({ ...rule, [name]: value });
  };

  const chooseType = (type) => {
    if (type === "contado") {
      setRule({
        ...rule,
        tipo: type,
        inicial_desde: 100,
        inicial_hasta: 100,
        meses_desde: 0,
        meses_hasta: 120,
        modo_descuento: "fijo",
        base_descuento: "precio_total",
      });
      return;
    }
    setRule({
      ...rule,
      tipo: type,
      meses_desde: 1,
      meses_hasta: 84,
      base_descuento: "saldo",
    });
  };

  const saveRule = async (event) => {
    event.preventDefault();
    setMessage("");
    try {
      await api.post("/promotions", rule);
      setRule(blankRule);
      setMessage("Regla comercial creada.");
      loadRules();
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  const toggleRule = async (item) => {
    try {
      await api.put(`/promotions/${item.id}`, { ...normalizeRule(item), activo: !item.activo });
      loadRules();
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  const removeRule = async (item) => {
    if (!window.confirm(`¿Eliminar la regla ${item.nombre}?`)) return;
    await api.delete(`/promotions/${item.id}`);
    loadRules();
  };

  return (
    <section className="commercial-rules-panel" data-testid="commercial-rules-panel">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">REGLAS COMERCIALES</span>
          <h2>Descuentos y condiciones</h2>
        </div>
        <BadgePercent size={21} />
      </div>
      <p className="rules-intro" data-testid="commercial-rules-description">
        Define qué condición debe cumplir el cliente, dónde se descuenta y si puede sumarse a otra regla.
      </p>
      <form className="commercial-rule-form" data-testid="commercial-rule-form" onSubmit={saveRule}>
        <div className="rule-field rule-name-field">
          <label htmlFor="rule-name">Nombre de la regla</label>
          <input data-testid="rule-name-input" id="rule-name" onChange={(e) => change("nombre", e.target.value)} placeholder="Ej. Financiación preferencial" required value={rule.nombre} />
        </div>
        <div className="rule-type-field">
          <span>Tipo de pago</span>
          <div className="rule-type-toggle">
            <button className={rule.tipo === "financiado" ? "active" : ""} data-testid="rule-financed-button" onClick={() => chooseType("financiado")} type="button">Financiado</button>
            <button className={rule.tipo === "contado" ? "active" : ""} data-testid="rule-cash-button" onClick={() => chooseType("contado")} type="button">Contado 100%</button>
          </div>
        </div>
        <div className="rule-field"><label htmlFor="rule-start">Vigente desde</label><input data-testid="rule-start-date-input" id="rule-start" onChange={(e) => change("fecha_inicio", e.target.value)} required type="date" value={rule.fecha_inicio} /></div>
        <div className="rule-field"><label htmlFor="rule-end">Vigente hasta</label><input data-testid="rule-end-date-input" id="rule-end" onChange={(e) => change("fecha_fin", e.target.value)} required type="date" value={rule.fecha_fin} /></div>
        <fieldset className="rule-group initial-group"><legend>Rango de inicial requerido</legend><label>Desde %<input data-testid="rule-initial-min-input" max="100" min="0" onChange={(e) => change("inicial_desde", Number(e.target.value))} type="number" value={rule.inicial_desde} /></label><label>Hasta %<input data-testid="rule-initial-max-input" max="100" min="0" onChange={(e) => change("inicial_hasta", Number(e.target.value))} type="number" value={rule.inicial_hasta} /></label></fieldset>
        {rule.tipo === "financiado" && <fieldset className="rule-group months-group"><legend>Plazo financiado permitido</legend><label>Desde meses<input data-testid="rule-months-min-input" max="120" min="1" onChange={(e) => change("meses_desde", Number(e.target.value))} type="number" value={rule.meses_desde} /></label><label>Hasta meses<input data-testid="rule-months-max-input" max="120" min="1" onChange={(e) => change("meses_hasta", Number(e.target.value))} type="number" value={rule.meses_hasta} /></label></fieldset>}
        <div className="rule-field"><label htmlFor="rule-discount-mode">Cómo se calcula el descuento</label><select data-testid="rule-discount-mode-select" id="rule-discount-mode" onChange={(e) => { const next = e.target.value; setRule({ ...rule, modo_descuento: next, tipo: next === "igual_inicial" ? "financiado" : rule.tipo, base_descuento: next === "igual_inicial" ? "saldo" : rule.base_descuento }); }} value={rule.modo_descuento}><option value="fijo">Porcentaje fijo definido abajo</option><option value="igual_inicial">Recibe lo que das: mismo % que la inicial</option></select><small>{rule.modo_descuento === "igual_inicial" ? "Ej.: entrega 20% y recibe 20% de descuento sobre el saldo." : "Usa el porcentaje de descuento indicado."}</small></div>
        {rule.modo_descuento === "igual_inicial" ? <div className="rule-explainer" data-testid="same-initial-rule-explainer"><strong>Recibe lo que das</strong><span>El descuento será igual al porcentaje real de inicial del cliente y se aplicará al saldo a financiar.</span></div> : <div className="rule-field"><label htmlFor="rule-discount">Descuento de la regla</label><input data-testid="rule-discount-input" id="rule-discount" max="100" min="0" onChange={(e) => change("descuento_porcentaje", Number(e.target.value))} type="number" value={rule.descuento_porcentaje} /><small>Porcentaje a descontar</small></div>}
        <div className="rule-field"><label htmlFor="rule-base">Aplicar descuento sobre</label><select data-testid="rule-discount-base-select" disabled={rule.modo_descuento === "igual_inicial"} id="rule-base" onChange={(e) => change("base_descuento", e.target.value)} value={rule.base_descuento}><option value="saldo">Saldo después de la inicial</option><option value="precio_total">Precio total del lote</option></select><small>{rule.modo_descuento === "igual_inicial" ? "Esta regla usa siempre el saldo financiado." : "Elige la base de cálculo"}</small></div>
        <label className="rule-checkbox"><input checked={rule.acumulable} data-testid="rule-stackable-checkbox" onChange={(e) => change("acumulable", e.target.checked)} type="checkbox" /><span><strong>Puede acumularse</strong><small>Se suma con otras reglas marcadas como acumulables.</small></span></label>
        <label className="rule-checkbox rule-status-checkbox"><input checked={rule.activo} data-testid="rule-active-checkbox" onChange={(e) => change("activo", e.target.checked)} type="checkbox" /><span><strong>Usar esta regla</strong><small>Desmárcala para guardarla sin que se aplique en cotizaciones.</small></span></label>
        <button className="dark-button rule-save-button" data-testid="commercial-rule-save-button" type="submit"><Plus size={17} /> Crear regla comercial</button>
      </form>
      {message && <p className="form-note" data-testid="commercial-rule-message">{message}</p>}
      <div className="rules-list" data-testid="commercial-rules-list">
        {rules.map((item) => <article className="commercial-rule-row" data-testid={`commercial-rule-${item.id}`} key={item.id}><div><strong>{item.nombre}</strong><p>{ruleDescription(item)}</p><span>{item.modo_descuento === "igual_inicial" ? "Descuento igual a la inicial sobre el saldo" : `${item.descuento_porcentaje}% sobre ${item.base_descuento === "saldo" ? "el saldo" : "el precio total"}`} · {item.acumulable ? "Acumulable" : "No acumulable"}</span></div><button className={item.activo ? "active-state" : "inactive-state"} data-testid={`commercial-rule-toggle-${item.id}`} onClick={() => toggleRule(item)} type="button">{item.activo ? "Activa" : "Inactiva"}</button><button aria-label="Eliminar regla comercial" className="icon-button danger" data-testid={`commercial-rule-delete-${item.id}`} onClick={() => removeRule(item)} type="button"><Trash2 size={16} /></button></article>)}
        {!rules.length && <p className="empty-inline" data-testid="commercial-rules-empty">No hay reglas comerciales configuradas.</p>}
      </div>
    </section>
  );
}
